import { observer } from 'mobx-react'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import appleStoreImgFr from '../../../assets/icons/app-store-fr.png'
import appleStoreImgEn from '../../../assets/icons/apple-store.svg'
import googlePlayStoreImgFr from '../../../assets/icons/google-playstore-fr.svg'
import googlePlayStoreImgEn from '../../../assets/icons/google-playstore.svg'

import DOMPurify from 'dompurify'
import Logo from '../../../assets/logo/logo-dark.svg'
import { classNames } from '../../../utils/misc/class-names'

const loginUrlMap = {
    '/coachee/login': import.meta.env.VITE_APP_COACHEE_WEB_LOGIN_URL,
    '/coachee/verify-password': import.meta.env
        .VITE_APP_COACHEE_WEB_VERIFY_PASSWORD_URL,
    '/coachee/auth/okta/sso': import.meta.env
        .VITE_APP_COACHEE_WEB_OKTA_LOGIN_URL,
    '/coachee/auth/sama/sso': import.meta.env
        .VITE_APP_COACHEE_WEB_AUTH_PROVIDOR_LOGIN_URL,
}

const allowedParams = ['details', 'phoneNumber', 'magicToken']

export const LoginCoacheePage: React.FC = observer(() => {
    const { t, i18n } = useTranslation()
    const [search] = useSearchParams()
    const token = search.get('magicToken') ?? ''
    const language = i18n.resolvedLanguage

    const playStoreAppLink =
        language === 'en'
            ? import.meta.env.VITE_APP_EN_PLAY_STORE_URL
            : import.meta.env.VITE_APP_FR_PLAY_STORE_URL

    const appStoreAppLink =
        language === 'en'
            ? import.meta.env.VITE_APP_EN_APP_STORE_URL
            : import.meta.env.VITE_APP_FR_APP_STORE_URL

    const googlePlayImg =
        language === 'en' ? googlePlayStoreImgEn : googlePlayStoreImgFr

    const appleStoreImg = language === 'en' ? appleStoreImgEn : appleStoreImgFr

    const getUrl = useCallback(() => {
        let url =
            loginUrlMap[window.location.pathname as keyof typeof loginUrlMap] ||
            ''
        if (token) {
            url = import.meta.env.VITE_APP_COACHEE_WEB_MAGIC_LOGIN_URL
        }
        const urlObject = new URL(url)

        urlObject.searchParams.set('do_login', 'true')

        allowedParams.forEach((param) => {
            if (search.has(param)) {
                urlObject.searchParams.set(param, search.get(param) ?? '')
            }
        })

        return DOMPurify.sanitize(urlObject.toString())
    }, [search, token])

    return (
        <main className="flex flex-col items-center justify-center h-screen w-full">
            <div
                className={classNames(
                    'lg:w-[550px] px-8 h-[450px]',
                    'flex flex-col items-center justify-center bg-[#f9fbfc]',
                )}
            >
                <img src={Logo} width={80} alt={t('alt.logo')} />
                <h3 className="lg:text-[2rem] leading-[40px] text-center text-[1.5rem] mt-3">
                    {t('titles.logIn')}
                </h3>
                <span className="text-[15px] -mt-2 text-primary">
                    {t('subTitles.welcome')}
                </span>
                <a
                    href={getUrl()}
                    className={classNames(
                        'text-[#ffffff] text-[17px]',
                        'px-[48px] mt-6 mb-10 py-3',
                        'rounded-full',
                        'bg-primary active:bg-accent-2',
                        'disabled:bg-[#f9fbfc]',
                        'disabled:text-[#a5b2b6]',
                        'h-[50px] text-center w-[80%] lg:w-[380px]',
                    )}
                >
                    {t('buttons.continue')}
                </a>
                <span className="text-[12px] font-[600] uppercase  text-primary">
                    {t('subTitles.appDownload')}
                </span>
                <div className="flex space-x-2 mt-3 items-center">
                    <a target="_blank" href={encodeURI(appStoreAppLink)}>
                        <img
                            src={appleStoreImg}
                            alt="apply-playtore"
                            width={150}
                        />
                    </a>
                    <a href={encodeURI(playStoreAppLink)} target="_blank">
                        <img
                            src={googlePlayImg}
                            alt="google-playStore"
                            width={150}
                        />
                    </a>
                </div>
            </div>
        </main>
    )
})

export default LoginCoacheePage
